import React, { useState } from "react";
import { toast } from "sonner";
import { WarningCircle, Trash, CheckCircle, CircleNotch } from "@phosphor-icons/react";
import api from "../../lib/api";
import InfoPage from "./InfoPage";

/**
 * Apple Guideline 5.1.1(v) — In-app account deletion.
 *
 * Three-stage flow:
 *   1. "warning"  — what gets deleted, what stays (Apple billing).
 *   2. "confirm"  — final "Delete my account" button + spinner.
 *   3. "success"  — confirmation, then signs out + clears local data
 *                   (handled by the parent's `onDeleted` callback).
 *
 * The backend endpoint `DELETE /api/auth/me` hard-deletes:
 *   • users (the account row)
 *   • user_sessions (every active session token)
 *   • tip_logs (saved tip history)
 *   • vaults (secure travel document vault)
 *   • referrals (both sides)
 *   • usage (per-day quota counters)
 *   • events (analytics tied to user_id)
 *
 * Sign in with Apple identity tokens are NOT stored server-side
 * (only the opaque `apple_sub` claim, removed with the users row).
 * Per Apple guidance, no external Apple token revocation is needed.
 *
 * Apple subscription billing is NOT canceled by this flow — App
 * Review explicitly requires we disclose that the user must cancel
 * separately via iOS Settings → Apple ID → Subscriptions.
 */
export default function DeleteAccountFlow({ onBack, onDeleted }) {
  const [stage, setStage] = useState("warning");
  const [busy, setBusy] = useState(false);

  async function handleDelete() {
    if (busy) return;
    setBusy(true);
    try {
      await api.delete("/auth/me");
      setStage("success");
    } catch (e) {
      const detail = e?.response?.data?.detail || "Could not delete account. Try again.";
      toast.error(detail);
      setBusy(false);
    }
  }

  if (stage === "warning") {
    return (
      <InfoPage title="Delete account" onBack={onBack}>
        <div
          className="bg-white rounded-2xl shadow-soft border border-navy/5 p-5 space-y-3 font-jakarta text-sm text-navy"
          data-testid="delete-account-warning"
        >
          <div className="flex items-center gap-2 text-orange font-extrabold text-base">
            <WarningCircle size={20} weight="fill" />
            This is permanent
          </div>
          <p>
            Deleting your TipLingo account permanently removes the following
            from our servers:
          </p>
          <ul className="list-disc ml-5 space-y-1 text-navy/90">
            <li>Your account profile and sign-in details</li>
            <li>Saved tip history and notes</li>
            <li>Saved phrasebook entries</li>
            <li>Travel document vault and stored documents</li>
            <li>Any referral / invite records</li>
            <li>Active sessions on every device</li>
          </ul>
          <p className="text-navy-soft text-xs leading-relaxed">
            <b>Apple subscription billing is managed separately.</b> Deleting
            your TipLingo account does <b>not</b> cancel any active Apple
            subscription. To stop future renewal, open iOS Settings → tap
            your Apple ID at the top → Subscriptions → TipLingo → Cancel
            Subscription before deleting your TipLingo account.
          </p>
          <p className="text-navy-soft text-xs leading-relaxed">
            Sign in with Apple credentials are not stored on our servers, so
            no external token revocation is needed.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setStage("confirm")}
          data-testid="delete-account-continue-btn"
          className="w-full mt-2 bg-white border border-navy/15 rounded-2xl shadow-soft py-4 flex items-center justify-center gap-2 text-navy font-jakarta font-bold"
        >
          Continue
        </button>
        <button
          type="button"
          onClick={onBack}
          data-testid="delete-account-cancel-btn"
          className="w-full bg-orange text-white rounded-2xl shadow-soft py-4 flex items-center justify-center gap-2 font-jakarta font-bold"
        >
          Keep my account
        </button>
      </InfoPage>
    );
  }

  if (stage === "confirm") {
    return (
      <InfoPage title="Delete account" onBack={onBack}>
        <div
          className="bg-white rounded-2xl shadow-soft border border-orange/30 p-5 space-y-3 font-jakarta text-sm text-navy"
          data-testid="delete-account-confirm"
        >
          <div className="flex items-center gap-2 text-orange font-extrabold text-base">
            <Trash size={20} weight="fill" />
            Last chance
          </div>
          <p>
            Once you tap <b>Delete my account</b> this cannot be undone. Your
            account record and saved app data will be permanently removed.
          </p>
        </div>
        <button
          type="button"
          onClick={handleDelete}
          disabled={busy}
          data-testid="delete-account-confirm-btn"
          className="w-full mt-2 bg-orange text-white rounded-2xl shadow-soft py-4 flex items-center justify-center gap-2 font-jakarta font-extrabold disabled:opacity-60"
        >
          {busy && <CircleNotch size={16} weight="bold" className="animate-spin" />}
          Delete my account
        </button>
        <button
          type="button"
          onClick={() => setStage("warning")}
          disabled={busy}
          data-testid="delete-account-back-btn"
          className="w-full bg-white border border-navy/15 rounded-2xl shadow-soft py-4 flex items-center justify-center gap-2 text-navy font-jakarta font-bold disabled:opacity-60"
        >
          Go back
        </button>
      </InfoPage>
    );
  }

  // success
  return (
    <InfoPage title="Account deleted" onBack={onDeleted}>
      <div
        className="bg-white rounded-2xl shadow-soft border border-forest/20 p-5 space-y-3 font-jakarta text-sm text-navy"
        data-testid="delete-account-success"
      >
        <div className="flex items-center gap-2 text-forest font-extrabold text-base">
          <CheckCircle size={22} weight="fill" />
          Your account is deleted
        </div>
        <p>
          Your TipLingo account record and saved app data have been removed
          from our servers. We've signed you out on this device.
        </p>
        <p className="text-navy-soft text-xs leading-relaxed">
          If you had an active Apple subscription, remember to cancel it via
          iOS Settings → Apple ID → Subscriptions if you haven't already.
        </p>
      </div>
      <button
        type="button"
        onClick={onDeleted}
        data-testid="delete-account-done-btn"
        className="w-full mt-2 bg-navy text-white rounded-2xl shadow-soft py-4 flex items-center justify-center gap-2 font-jakarta font-extrabold"
      >
        Done
      </button>
    </InfoPage>
  );
}
